import CloudDownloadIcon from "@mui/icons-material/CloudDownload";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import HeadsetIcon from "@mui/icons-material/Headset";
import OfflinePinIcon from "@mui/icons-material/OfflinePin";
import LinkIcon from "@mui/icons-material/Link";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Avatar,
  Box,
  Divider,
  List,
  ListItem,
  Snackbar,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import Tooltip from "@mui/material/Tooltip";
import React, { useEffect, useState, useRef, forwardRef, useImperativeHandle } from "react";
import AudioPlayer from "react-h5-audio-player";
import { Link } from "react-router-dom";
import "./digitalSidebar.css";

import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import IconButton from "@mui/material/IconButton";

const DigitalSidebar = forwardRef(({ book, setPageNumber, onToggle, currentPage }, ref) => {
  const [selectedTab, setSelectedTab] = useState(0);
  const [cachedFiles, setCachedFiles] = useState({});
  const [snackbarOpenMedia, setSnackbarOpenMedia] = useState(false);
  const [expandedAccordions, setExpandedAccordions] = useState({});
  const [isOpen, setIsOpen] = useState(true);
  const [downloadJobs, setDownloadJobs] = useState({});
  
  // Create refs for each accordion
  const accordionRefs = useRef({});
  // Add a ref for the sidebar content container
  const sidebarContentRef = useRef(null);
  
  // Use effect to watch for page number changes
  useEffect(() => {
    if (currentPage && accordionRefs.current[currentPage]) {
      // Expand the accordion if it's not already expanded
      if (!expandedAccordions[currentPage]) {
        setExpandedAccordions(prev => ({
          ...prev,
          [currentPage]: true
        }));
      }
      
      // Wait a bit for accordion to expand
      setTimeout(() => {
        if (!sidebarContentRef.current || !accordionRefs.current[currentPage]) return;
        
        // Get the container and target element positions
        const container = sidebarContentRef.current;
        const target = accordionRefs.current[currentPage];
        
        // Calculate positions
        const containerRect = container.getBoundingClientRect();
        const targetRect = target.getBoundingClientRect();
        
        // Calculate scroll position - center the target in the container
        const scrollTop = 
          target.offsetTop - 
          container.offsetTop - 
          (containerRect.height / 2) + 
          (targetRect.height / 2);
        
        // Scroll just the sidebar content
        container.scrollTo({
          top: scrollTop,
          behavior: 'smooth'
        });
      }, 100);
    }
  }, [currentPage, expandedAccordions]);

  const handleTabChange = (event, newValue) => {
    setSelectedTab(newValue);
  };

  const handleToggle = () => {
    setIsOpen(!isOpen);
  };
  const handleAudioPlay = (voiceFileName) => {
    const audioElements = document.getElementsByTagName("audio");
    for (let i = 0; i < audioElements.length; i++) {
      const audioElement = audioElements[i];
      if (voiceFileName === audioElement.src) {
        audioElement.play();
      } else {
        audioElement.pause();
      }
    }
  };

  const toggleAudioControls = (page, pageNumber) => {
    console.log(page, pageNumber, 'page and page number');
    const audioControls = document.querySelector(
      `.audio-play-buttons_${page}_${pageNumber}`
    );
    console.log(audioControls.style.display, 'audio controls before');
    audioControls.style.display =
      audioControls.style.display === "none" ? "block" : "none";
  };


  // SW message listener for per-item progress
  useEffect(() => {
    const onMessage = (event) => {
      const data = event.data || {};
      const { type, url, receivedBytes, totalBytes, percent } = data;
      if (!url) return;
      setDownloadJobs((prev) => {
        const next = { ...prev };
        if (type === 'download-start') {
          next[url] = { status: 'downloading', receivedBytes: 0, totalBytes: totalBytes || 0, percent: 0 };
        } else if (type === 'download-progress') {
          next[url] = { status: 'downloading', receivedBytes: receivedBytes || 0, totalBytes: totalBytes || 0, percent: percent || 0 };
        } else if (type === 'download-complete') {
          next[url] = { status: 'verifying', receivedBytes: totalBytes || 0, totalBytes: totalBytes || 0, percent: 100 };
          // verify and flip to done
          verifyCachedMedia(url).then((ok) => {
            setDownloadJobs((prev2) => ({ ...prev2, [url]: { status: ok ? 'done' : 'failed', receivedBytes: totalBytes || 0, totalBytes: totalBytes || 0, percent: 100 } }));
            if (ok) setCachedFiles((prevC) => ({ ...prevC, [url]: true }));
          });
        } else if (type === 'download-error') {
          next[url] = { status: 'failed', receivedBytes: 0, totalBytes: 0, percent: 0 };
        }
        return next;
      });
    };
    navigator.serviceWorker?.addEventListener('message', onMessage);
    return () => navigator.serviceWorker?.removeEventListener('message', onMessage);
  }, []);

  // Helper to ensure we always use the CloudFront URL for caching and playback
  const getCloudFrontUrl = (url) => {
    if (!url) return "";
    return url.replace(
      "https://redsea-books-assets.s3.eu-central-1.amazonaws.com",
      "https://dng2cduv9v2yj.cloudfront.net"
    );
  };

  const verifyCachedMedia = async (fileUrl) => {
    try {
      const cache = await caches.open('media-cache-v1');
      const resp = await cache.match(getCloudFrontUrl(fileUrl), { ignoreSearch: true, ignoreVary: true });
      if (!resp) return false;
      if (resp.type === 'opaque') return true;
      const blob = await resp.blob();
      return !!blob && blob.size > 0;
    } catch (e) {
      return false;
    }
  };

  // Check if the media is already cached when the component mounts
  useEffect(() => {
    if (book?.pages) {
      book.pages.forEach((page) => {
        page.voiceFiles?.forEach((voiceFile) => {
          checkIfMediaIsCached(getCloudFrontUrl(voiceFile.url));
        });
        page.videoFiles?.forEach((videoFile) => {
          checkIfMediaIsCached(getCloudFrontUrl(videoFile.url), "video");
        });
      });
    }
  }, [book]);

  // Check if the media file is cached and update cached status accordingly
  const checkIfMediaIsCached = async (fileUrl, fileType) => {
    const cacheName = "media-cache-v1";

    try {
      const cache = await caches.open(cacheName);
      const cachedResponse = await cache.match(fileUrl, { ignoreSearch: true, ignoreVary: true });

      setCachedFiles((prev) => ({
        ...prev,
        [fileUrl]: !!cachedResponse,
      }));
    } catch (error) {
      console.error(`Error checking ${fileType} cache:`, error);
    }
  };

  // Request SW to download a media file with progress
  const handleCacheMedia = async (fileUrl) => {
    if (navigator.serviceWorker && navigator.serviceWorker.controller) {
      navigator.serviceWorker.controller.postMessage({ action: 'downloadUrl', url: fileUrl });
    }
  };

  // Handle snackbar close
  const handleSnackbarClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setSnackbarOpenMedia(false);
  };
  // handles  accordion expansion
  const handleAccordionChange = (pageNumber) => (event, isExpanded) => {
    setExpandedAccordions((prev) => ({
      ...prev,
      [pageNumber]: isExpanded,
    }));
  };

  const handleAudioPlayerClick = (e) => {
    e.stopPropagation();
    e.preventDefault();
  };

  // Create a handler function for the download icon
  const handleDownloadClick = (e, fileUrl) => {
    e.stopPropagation(); // Stop event from bubbling up to accordion
    handleCacheMedia(fileUrl);
  };

  // Create a handler for the offline pin icon
  const handleOfflinePinClick = (e) => {
    e.stopPropagation(); // Stop event from bubbling up to accordion
  };

  return (
    <div className={`content-wrapper ${!isOpen ? "closed" : "open"}`}>
      <IconButton
        className="side-toggle-button"
        sx={{ color: "White", backgroundColor: "#8b6f24" }}
        onClick={handleToggle}
        size="small"
      >
        {isOpen ? <ChevronLeftIcon /> : <ChevronRightIcon />}
      </IconButton>
      <Box 
        className={`digital-sidebar-container ${isOpen ? "" : "closed"}`}
        ref={sidebarContentRef} // Add ref to the scrollable container
      >
        <>
          {/* Snackbar Notification */}
          <Snackbar
            open={snackbarOpenMedia}
            autoHideDuration={6000}
            onClose={handleSnackbarClose}
            anchorOrigin={{ vertical: "top", horizontal: "right" }} // Top-right position
          >
            <Alert
              onClose={handleSnackbarClose}
              severity="success"
              sx={{ width: "100%" }}
            >
              Media downloaded successfully!
            </Alert>
          </Snackbar>
          {/* Book Thumbnail and Title */}
          <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
            <Link to={`/bookDetails/${book?._id}`}>
              <Avatar
                variant="square"
                src={(book?.thumbnail || "https://dng2cduv9v2yj.cloudfront.net/default-thumbnail.png").replace(
                  "https://redsea-books-assets.s3.eu-central-1.amazonaws.com",
                  "https://dng2cduv9v2yj.cloudfront.net"
                )}
                alt="Book Thumbnail"
                sx={{ width: 60, height: 80, mr: 2 }}
              />
            </Link>
            <Typography variant="h6" sx={{ lineHeight: 1.2 }}>
              <Link
                to={`/bookDetails/${book?._id}`}
                className="book-media-link"
              >
                {" "}
                {book?.title}{" "}
              </Link>
            </Typography>
          </Box>

          {/* Tabs for Pages and Media */}
          <Tabs
            value={selectedTab}
            onChange={handleTabChange}
            sx={{ backgroundColor: "white", color: "black", mb: 2 }}
            textColor="inherit"
            indicatorColor="secondary"
          >
            <Tab label="Pages & Content" />
            <Tab label="Content" />
          </Tabs>

          {/* Divider */}
          <Divider sx={{ backgroundColor: "#8d711f", mb: 2 }} />
          {/* Conditional Rendering Based on Selected Tab */}
          {selectedTab === 0 && (
            <List>
              {[...Array(book?.digitalEditionPageNumber)].map((_, index) => {
                const page = book?.pages?.find((p) => p.number === index + 1);
                const pageNumber = index + 1;

                return (
                  <Accordion
                    key={index}
                    ref={el => accordionRefs.current[pageNumber] = el}
                    expanded={expandedAccordions[pageNumber] || false}
                    onChange={handleAccordionChange(pageNumber)}
                    sx={{
                      backgroundColor: "white",
                      color: "black",
                      boxShadow: "none",
                      mb: 1,
                    }}
                  >
                    <AccordionSummary
                      expandIcon={
                        page?.voiceFiles || page?.videoFiles || page?.hyperlinks ? (
                          <ExpandMoreIcon sx={{ color: "black" }} />
                        ) : null
                      }
                      onClick={(e) => {
                        // Only navigate to page if not clicking on the expand icon
                        if (!e.target.closest('.MuiAccordionSummary-expandIconWrapper')) {
                          setPageNumber(pageNumber);
                        }
                      }}
                      aria-controls={`panel${pageNumber}a-content`}
                      id={`panel${pageNumber}a-header`}
                    >
                      <Typography>Page {pageNumber}</Typography>{" "}
                      {/* Display page number */}
                    </AccordionSummary>

                    {page ? (
                      <>
                        {/* Voice files rendering -Expaneded only -*/}
                        {expandedAccordions[pageNumber] && page.voiceFiles && (
                          <AccordionDetails style={{ padding: "10px 0px" }}>
                            {page.voiceFiles.map((voiceFile, voiceIndex) => {
                              const cloudFrontVoiceUrl = getCloudFrontUrl(voiceFile.url);
                              if (!cloudFrontVoiceUrl) return null;
                              // Base64 encode the voice file URL
                              const encodedVoiceFileUrl = btoa(cloudFrontVoiceUrl);
                              return (
                                <ListItem
                                  key={voiceIndex}
                                  style={{ display: "block" }}
                                  onClick={(e) => {
                                    // Only toggle if not clicking on the audio player
                                    if (!e.target.closest('.rhap_container')) {
                                      toggleAudioControls(
                                        voiceIndex,
                                        page.number
                                      );
                                    }
                                  }}
                                  button
                                >
                                  <div className="audio-file-title">
                                    <HeadsetIcon />
                                    <p style={{ cursor: "pointer" }}>
                                      {voiceFile.name
                                        ? voiceFile.name
                                        : decodeURIComponent(
                                            voiceFile.url
                                              ?.split("/")
                                              ?.pop()
                                              ?.replace(".mp3", "")
                                              ?.replace(/_/g, " ")
                                              ?.replace(/\+/g, " ")
                                          )}
                                    </p>
                                  </div>
                                  <div
                                    style={{ display: "none" }}
                                    className={`audio-play-buttons_${voiceIndex}_${page.number}`}
                                    onClick={handleAudioPlayerClick}
                                  >
                                    <AudioPlayer
                                      src={atob(encodedVoiceFileUrl)}
                                      className={`audio_${page.number}`}
                                      onPlay={(e) => {
                                        e.stopPropagation(); // Stop propagation on play
                                        handleAudioPlay(atob(encodedVoiceFileUrl));
                                      }}
                                      onPause={(e) => e.stopPropagation()} // Stop propagation on pause
                                      onClickPrev={(e) => e.stopPropagation()} // Stop propagation on previous
                                      onClickNext={(e) => e.stopPropagation()} // Stop propagation on next
                                      progressJumpStep={5000}
                                      showVolumeControl={false}
                                    />
                                    {downloadJobs[atob(encodedVoiceFileUrl)]?.status === 'downloading' && (
                                      <div style={{ fontSize: '0.8rem', marginTop: 4 }}>Downloading {downloadJobs[atob(encodedVoiceFileUrl)].percent || 0}%</div>
                                    )}
                                  </div>
                                  <Tooltip
                                    title={
                                      cachedFiles[voiceFile.url]
                                        ? "Audio is cached and available offline"
                                        : "Cache this audio file"
                                    }
                                  >
                                    {cachedFiles[voiceFile.url] ? (
                                      <span onClick={handleOfflinePinClick}>
                                        <OfflinePinIcon />
                                      </span>
                                    ) : (
                                      <span onClick={(e) => handleDownloadClick(e, atob(encodedVoiceFileUrl))}>
                                        <CloudDownloadIcon />
                                      </span>
                                    )}
                                  </Tooltip>
                                </ListItem>
                              );
                            })}
                          </AccordionDetails>
                        )}

                        {/* Video files rendering -Expaneded only-*/}
                        {expandedAccordions[pageNumber] && page.videoFiles && (
                          <AccordionDetails style={{ padding: "10px 0px" }}>
                            <ListItem style={{ display: "block" }}>
                              {page.videoFiles.map((videoFile, videoIndex) => {
                                const cloudFrontVideoUrl = getCloudFrontUrl(videoFile.url);
                                if (!cloudFrontVideoUrl) return null;
                                // Base64 encode the video file URL
                                const encodedVideoFileUrl = btoa(cloudFrontVideoUrl);

                                return (
                                  <div key={videoIndex}>
                                    <video
                                      controls
                                      className="video-file-digitalsidebar"
                                      src={atob(encodedVideoFileUrl)}
                                    ></video>
                                    <br />
                                    <Tooltip
                                      title={
                                        cachedFiles[atob(encodedVideoFileUrl)]
                                          ? "Video is cached and available offline"
                                          : "Cache this video"
                                      }
                                    >
                                      {cachedFiles[
                                        atob(encodedVideoFileUrl)
                                      ] ? (
                                        <span onClick={handleOfflinePinClick}>
                                          <OfflinePinIcon />
                                        </span>
                                      ) : (
                                        <span onClick={(e) => handleDownloadClick(e, atob(encodedVideoFileUrl))}>
                                          <CloudDownloadIcon />
                                        </span>
                                      )}
                                    </Tooltip>
                                    {downloadJobs[atob(encodedVideoFileUrl)]?.status === 'downloading' && (
                                      <div style={{ fontSize: '0.8rem', marginTop: 4 }}>Downloading {downloadJobs[atob(encodedVideoFileUrl)].percent || 0}%</div>
                                    )}
                                  </div>
                                );
                              })}
                            </ListItem>
                          </AccordionDetails>
                        )}

                        {/* Hyperlinks rendering -Expanded only- */}
                        {expandedAccordions[pageNumber] && page.hyperlinks && (
                          <AccordionDetails style={{ padding: "10px 0px" }}>
                            {page.hyperlinks.map((hyperlink, linkIndex) => (
                              <ListItem
                                key={linkIndex}
                                style={{ display: "block", padding: "8px 0px" }}
                              >
                                <div className="hyperlink-item" style={{ display: "flex", alignItems: "center" }}>
                                  <LinkIcon style={{ marginRight: "8px", color: "#8b6f24" }} />
                                  <a
                                    href={hyperlink.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{
                                      color: "#8b6f24",
                                      textDecoration: "none",
                                      fontSize: "0.9rem",
                                      cursor: "pointer"
                                    }}
                                    onMouseEnter={(e) => e.target.style.textDecoration = "underline"}
                                    onMouseLeave={(e) => e.target.style.textDecoration = "none"}
                                  >
                                    {hyperlink.name || hyperlink.title || hyperlink.url}
                                  </a>
                                </div>
                              </ListItem>
                            ))}
                          </AccordionDetails>
                        )}
                      </>
                    ) : (
                      <AccordionDetails>
                        <ListItem
                          button
                          onClick={() => setPageNumber(pageNumber)} // Allow clicking on the item to navigate to the page in the PDF
                          style={{ cursor: "pointer" }}
                        >
                          <Typography>
                            No content available for Page {pageNumber}
                          </Typography>
                        </ListItem>
                      </AccordionDetails>
                    )}
                  </Accordion>
                );
              })}
            </List>
          )}
          {selectedTab === 1 && (
            <List>
              {book?.pages
                .filter(
                  (page) =>
                    page?.voiceFiles?.length > 0 || page?.videoFiles?.length > 0 || page?.hyperlinks?.length > 0 // Only pages with content
                )
                .map((page, index) => {
                  return (
                    <Accordion
                      key={index}
                      ref={el => accordionRefs.current[page.number] = el}
                      expanded={expandedAccordions[page.number] || false}
                      onChange={handleAccordionChange(page.number)}
                      sx={{
                        backgroundColor: "white",
                        color: "black",
                        boxShadow: "none",
                        mb: 1,
                      }}
                    >
                      <AccordionSummary
                        expandIcon={
                          page?.voiceFiles || page?.videoFiles || page?.hyperlinks ? (
                            <ExpandMoreIcon sx={{ color: "black" }} />
                          ) : null
                        }
                        onClick={(e) => {
                          // Only navigate to page if not clicking on the expand icon
                          if (!e.target.closest('.MuiAccordionSummary-expandIconWrapper')) {
                            setPageNumber(page.number);
                          }
                        }}
                        aria-controls={`panel${page.number}a-content`}
                        id={`panel${page.number}a-header`}
                      >
                        <Typography>Page {page.number}</Typography>{" "}
                        {/* Display page number */}
                      </AccordionSummary>

                      {page ? (
                        <>
                          {/* Voice files rendering - Expanded only */}
                          {expandedAccordions[page.number] &&
                            page.voiceFiles && (
                              <AccordionDetails style={{ padding: "10px 0px" }}>
                                {page.voiceFiles.map(
                                  (voiceFile, voiceIndex) => {
                                    const cloudFrontVoiceUrl = getCloudFrontUrl(voiceFile.url);
                                    if (!cloudFrontVoiceUrl) return null;
                                    // Base64 encode the voice file URL
                                    const encodedVoiceFileUrl = btoa(cloudFrontVoiceUrl);
                                    return (
                                      <ListItem
                                        key={voiceIndex}
                                        style={{ display: "block" }}
                                        onClick={(e) => {
                                          // Only toggle if not clicking on the audio player
                                          if (!e.target.closest('.rhap_container')) {
                                            toggleAudioControls(
                                              voiceIndex,
                                              page.number
                                            );
                                          }
                                        }}
                                        button
                                      >

                                        <div className="audio-file-title">
                                          <HeadsetIcon />
                                          <p style={{ cursor: "pointer" }}>
                                            {voiceFile.name
                                              ? voiceFile.name
                                              : decodeURIComponent(
                                                  voiceFile.url
                                                    ?.split("/")
                                                    ?.pop()
                                                    ?.replace(".mp3", "")
                                                    ?.replace(/_/g, " ")
                                                    ?.replace(/\+/g, " ")
                                                )}
                                          </p>
                                        </div>
                                        <div
                                          style={{ display: "none" }}
                                          className={`audio-play-buttons_${voiceIndex}_${page.number}`}
                                          onClick={handleAudioPlayerClick}
                                        >
                                          <AudioPlayer
                                            src={atob(encodedVoiceFileUrl)}
                                            className={`audio_${page.number}`}
                                            onPlay={(e) => {
                                              e.stopPropagation(); // Stop propagation on play
                                              handleAudioPlay(atob(encodedVoiceFileUrl));
                                            }}
                                            onPause={(e) => e.stopPropagation()} // Stop propagation on pause
                                            onClickPrev={(e) => e.stopPropagation()} // Stop propagation on previous
                                            onClickNext={(e) => e.stopPropagation()} // Stop propagation on next
                                            progressJumpStep={5000}
                                            showVolumeControl={false}
                                          />
                                          {downloadJobs[atob(encodedVoiceFileUrl)]?.status === 'downloading' && (
                                            <div style={{ fontSize: '0.8rem', marginTop: 4 }}>Downloading {downloadJobs[atob(encodedVoiceFileUrl)].percent || 0}%</div>
                                          )}
                                        </div>
                                        <Tooltip
                                          title={
                                            cachedFiles[cloudFrontVoiceUrl]
                                              ? "Audio is cached and available offline"
                                              : "Cache this audio file"
                                          }
                                        >
                                          {cachedFiles[cloudFrontVoiceUrl] ? (
                                            <span onClick={handleOfflinePinClick}>
                                              <OfflinePinIcon />
                                            </span>
                                          ) : (
                                            <span onClick={(e) => handleDownloadClick(e, cloudFrontVoiceUrl)}>
                                              <CloudDownloadIcon />
                                            </span>
                                          )}
                                        </Tooltip>
                                      </ListItem>
                                    );
                                  }
                                )}
                              </AccordionDetails>
                            )}

                          {/* Video files rendering - Expanded only */}
                          {expandedAccordions[page.number] &&
                            page.videoFiles && (
                              <AccordionDetails style={{ padding: "10px 0px" }}>
                                <ListItem style={{ display: "block" }}>
                                  {page.videoFiles.map(
                                    (videoFile, videoIndex) => {
                                      const cloudFrontVideoUrl = getCloudFrontUrl(videoFile.url);
                                      if (!cloudFrontVideoUrl) return null;
                                      // Base64 encode the video file URL
                                      const encodedVideoFileUrl = btoa(cloudFrontVideoUrl);

                                      return (
                                        <div key={videoIndex}>
                                          <video
                                            controls
                                            className="video-file-digitalsidebar"
                                            src={atob(encodedVideoFileUrl)}
                                          ></video>
                                          <br />
                                          <Tooltip
                                            title={
                                              cachedFiles[cloudFrontVideoUrl]
                                                ? "Video is cached and available offline"
                                                : "Cache this video file"
                                            }
                                          >
                                            {cachedFiles[cloudFrontVideoUrl] ? (
                                              <span onClick={handleOfflinePinClick}>
                                                <OfflinePinIcon />
                                              </span>
                                            ) : (
                                              <span onClick={(e) => handleDownloadClick(e, cloudFrontVideoUrl)}>
                                                <CloudDownloadIcon />
                                              </span>
                                            )}
                                          </Tooltip>
                                          {downloadJobs[atob(encodedVideoFileUrl)]?.status === 'downloading' && (
                                            <div style={{ fontSize: '0.8rem', marginTop: 4 }}>Downloading {downloadJobs[atob(encodedVideoFileUrl)].percent || 0}%</div>
                                          )}
                                        </div>
                                      );
                                    }
                                  )}
                                </ListItem>
                              </AccordionDetails>
                            )}

                          {/* Hyperlinks rendering - Expanded only */}
                          {expandedAccordions[page.number] && page.hyperlinks && (
                            <AccordionDetails style={{ padding: "10px 0px" }}>
                              {page.hyperlinks.map((hyperlink, linkIndex) => (
                                <ListItem
                                  key={linkIndex}
                                  style={{ display: "block", padding: "8px 0px" }}
                                >
                                  <div className="hyperlink-item" style={{ display: "flex", alignItems: "center" }}>
                                    <LinkIcon style={{ marginRight: "8px", color: "#8b6f24" }} />
                                    <a
                                      href={hyperlink.url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      style={{
                                        color: "#8b6f24",
                                        textDecoration: "none",
                                        fontSize: "0.9rem",
                                        cursor: "pointer"
                                      }}
                                      onMouseEnter={(e) => e.target.style.textDecoration = "underline"}
                                      onMouseLeave={(e) => e.target.style.textDecoration = "none"}
                                    >
                                      {hyperlink.name || hyperlink.title || hyperlink.url}
                                    </a>
                                  </div>
                                </ListItem>
                              ))}
                            </AccordionDetails>
                          )}

                          {/* Hyperlinks rendering - Expanded only */}
                          {expandedAccordions[page.number] && page.hyperlinks && (
                            <AccordionDetails style={{ padding: "10px 0px" }}>
                              {page.hyperlinks.map((hyperlink, linkIndex) => (
                                <ListItem
                                  key={linkIndex}
                                  style={{ display: "block", padding: "8px 0px" }}
                                >
                                  <div className="hyperlink-item" style={{ display: "flex", alignItems: "center" }}>
                                    <LinkIcon style={{ marginRight: "8px", color: "#8b6f24" }} />
                                    <a
                                      href={hyperlink.url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      style={{
                                        color: "#8b6f24",
                                        textDecoration: "none",
                                        fontSize: "0.9rem",
                                        cursor: "pointer"
                                      }}
                                      onMouseEnter={(e) => e.target.style.textDecoration = "underline"}
                                      onMouseLeave={(e) => e.target.style.textDecoration = "none"}
                                    >
                                      {hyperlink.name || hyperlink.title || hyperlink.url}
                                    </a>
                                  </div>
                                </ListItem>
                              ))}
                            </AccordionDetails>
                          )}
                        </>
                      ) : (
                        <AccordionDetails>
                          <ListItem
                            button
                            onClick={() => setPageNumber(page.number)} // Allow clicking on the item to navigate to the page in the PDF
                            style={{ cursor: "pointer" }}
                          >
                            <Typography>
                              No content available for Page {page.number}
                            </Typography>
                          </ListItem>
                        </AccordionDetails>
                      )}
                    </Accordion>
                  );
                })}
            </List>
          )}
        </>
      </Box>
    </div>
  );
});

export default DigitalSidebar;
